import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { getWeekStart, isInWeek, weekLabel } from '../utils/weekDates';

const API = process.env.REACT_APP_API_URL;

const STATUSES = ['New', 'Under Review', 'In Progress', 'Shipped', 'Declined'];
const COLUMN_WIDTH = 250;

// Display-only relabeling — the underlying status value stored on each item
// stays "New"/"Shipped" (matches VALID_STATUSES on the backend); only what's
// shown to the user changes.
const STATUS_LABELS = { New: 'Backlog', Shipped: 'Shipped This Week' };
const statusLabel = (status) => STATUS_LABELS[status] || status;

const CATEGORIES = [
  { value: 'feature',     label: 'Feature',     color: '#2c5aa0', bg: '#eef4ff' },
  { value: 'bug',         label: 'Bug',         color: '#c0392b', bg: '#fdedec' },
  { value: 'operational', label: 'Operational', color: '#555',    bg: '#eef2f6' },
  { value: 'data',        label: 'Data',        color: '#7d3c98', bg: '#f4ecf7' },
];
const categoryMeta = (value) => CATEGORIES.find(c => c.value === value) || CATEGORIES[0];

// Product-area tag — a fixed list matching the backend's VALID_THEMES
// (app/routers/backlog.py) exactly. "Other" is a real selectable value, not
// the fallback for an unthemed item — a NULL theme renders as "Untagged"
// (below) so an unthemed backlog stays visibly distinct from a genuinely
// "Other" one.
const THEMES = [
  { value: 'Lead QA & Data Quality',          label: 'Lead QA & Data Quality',          color: '#1e8449', bg: '#eafaf1' },
  { value: 'QA Rules & Targeting',            label: 'QA Rules & Targeting',            color: '#117a65', bg: '#e8f8f5' },
  { value: 'Client Delivery',                 label: 'Client Delivery',                 color: '#2c5aa0', bg: '#eef4ff' },
  { value: 'Program Setup & Spinup',          label: 'Program Setup & Spinup',          color: '#7d3c98', bg: '#f4ecf7' },
  { value: 'Reporting & Comms',               label: 'Reporting & Comms',               color: '#b9770e', bg: '#fef5e7' },
  { value: 'Admin & Ops Tooling',             label: 'Admin & Ops Tooling',             color: '#555',    bg: '#eef2f6' },
  { value: 'Portals (Supplier, PM, myHome)',  label: 'Portals (Supplier, PM, myHome)',  color: '#1a5276', bg: '#eaf2f8' },
  { value: 'ContentIQ Platform',              label: 'ContentIQ Platform',              color: '#a04000', bg: '#fdf2e9' },
  { value: 'Strategic Scale Initiatives',     label: 'Strategic Scale Initiatives',     color: '#922b21', bg: '#fdedec' },
  { value: 'Code Foundations',                label: 'Code Foundations',                color: '#5d6d7e', bg: '#f4f6f7' },
  { value: 'Other',                           label: 'Other',                           color: '#909497', bg: '#f2f3f4' },
];
const themeMeta = (value) => THEMES.find(t => t.value === value) || null;

const ASSIGNEES = ['Loria', 'Ed'];

const PRIORITIES = [
  { value: 'High',   label: 'High',   color: '#c0392b', bg: '#fdedec' },
  { value: 'Medium', label: 'Medium', color: '#b9770e', bg: '#fef5e7' },
  { value: 'Low',    label: 'Low',    color: '#2874a6', bg: '#eaf2f8' },
];
const priorityMeta = (value) => PRIORITIES.find(p => p.value === value) || null;
const PRIORITY_RANK = { High: 0, Medium: 1, Low: 2 };

// Sort methodologies for the board/list views. "order" is the manual drag
// rank (the only mode where drag-reorder makes sense — the other two would
// re-sort a dropped card right back out from under it, so drag is disabled
// there); "priority"/"owner" are read-only viewing arrangements layered on
// top, with the manual rank as the tiebreak within equal groups.
const SORT_MODES = [
  { value: 'order',    label: 'Order' },
  { value: 'priority', label: 'Priority' },
  { value: 'owner',    label: 'Owner' },
];

const btn = (bg) => ({ padding: '5px 12px', background: bg, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });
const input = { padding: '7px 9px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' };
const select = { ...input };

const fmt = (iso) => {
  if (!iso) return '—';
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  try {
    return new Date(s).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });
  } catch { return iso; }
};

function AddItemModal({ onClose, onCreate }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('feature');
  const [theme, setTheme] = useState('');
  const [priority, setPriority] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!title.trim()) { setError('A title is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await onCreate({ title: title.trim(), description: description.trim() || null, category, theme: theme || null, priority: priority || null });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not add this item.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
      onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '8px', padding: '22px', width: '480px', maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0, color: '#2c3e50' }}>Add a Backlog Item</h3>
        <p style={{ fontSize: '12px', color: '#999', marginTop: '-8px' }}>New items always start in the <b>Backlog</b> column.</p>
        {error && <div style={{ background: '#fdecea', color: '#c0392b', padding: '8px 12px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Title</label>
          <input style={{ ...input, width: '100%' }} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Add a filter for..." autoFocus />
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Description</label>
          <textarea style={{ ...input, width: '100%', minHeight: '90px', fontFamily: 'inherit', resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)}
            placeholder="What's needed, and why it matters — plain language, no need for technical detail." />
        </div>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '18px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Type</label>
            <select style={select} value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Priority</label>
            <select style={select} value={priority} onChange={e => setPriority(e.target.value)}>
              <option value="">Not set</option>
              {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginBottom: '18px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Theme</label>
          <select style={{ ...select, width: '100%' }} value={theme} onChange={e => setTheme(e.target.value)}>
            <option value="">Untagged</option>
            {THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button style={btn('#95a5a6')} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={btn('#27ae60')} onClick={submit} disabled={saving}>{saving ? 'Adding…' : 'Add Item'}</button>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ item, onClose, onUpdate, onDelete }) {
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description || '');
  const [category, setCategory] = useState(item.category);
  const [theme, setTheme] = useState(item.theme || '');
  const [status, setStatus] = useState(item.status);
  const [assignedTo, setAssignedTo] = useState(item.assigned_to || '');
  const [priority, setPriority] = useState(item.priority || '');
  const [adminNotes, setAdminNotes] = useState(item.admin_notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    if (!title.trim()) { setError('A title is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await onUpdate(item.id, {
        title: title.trim(),
        description: description.trim() || null,
        category,
        theme: theme || null,
        status,
        assigned_to: assignedTo || null,
        priority: priority || null,
        admin_notes: adminNotes.trim() || null,
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save this item.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    await onDelete(item);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
      onClick={onClose}>
      <div style={{ background: 'white', borderRadius: '8px', padding: '22px', width: '560px', maxWidth: '92vw', maxHeight: '86vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0, color: '#2c3e50' }}>Backlog Item Details</h3>
        {error && <div style={{ background: '#fdecea', color: '#c0392b', padding: '8px 12px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Title</label>
          <input style={{ ...input, width: '100%' }} value={title} onChange={e => setTitle(e.target.value)} />
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Description</label>
          <textarea style={{ ...input, width: '100%', minHeight: '110px', fontFamily: 'inherit', resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Type</label>
            <select style={{ ...select, width: '100%' }} value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Status</label>
            <select style={{ ...select, width: '100%' }} value={status} onChange={e => setStatus(e.target.value)}>
              {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Assigned to</label>
            <select style={{ ...select, width: '100%' }} value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
              <option value="">Unassigned</option>
              {ASSIGNEES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Priority</label>
            <select style={{ ...select, width: '100%' }} value={priority} onChange={e => setPriority(e.target.value)}>
              <option value="">Not set</option>
              {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Theme</label>
          <select style={{ ...select, width: '100%' }} value={theme} onChange={e => setTheme(e.target.value)}>
            <option value="">Untagged</option>
            {THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>Notes for the weekly review</label>
          <textarea style={{ ...input, width: '100%', minHeight: '70px', fontFamily: 'inherit', resize: 'vertical' }} value={adminNotes} onChange={e => setAdminNotes(e.target.value)} />
        </div>

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: '#999', marginBottom: '18px' }}>
          {item.requested_by_name && <span>👤 Requested by {item.requested_by_name}{item.requested_by_role ? ` (${item.requested_by_role})` : ''}</span>}
          <span>Added {fmt(item.created_at)}</span>
          <span>Updated {fmt(item.updated_at)}</span>
          {item.shipped_at && <span>Shipped {fmt(item.shipped_at)}</span>}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
          <button style={btn('#c0392b')} onClick={remove} disabled={saving}>Delete</button>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={btn('#95a5a6')} onClick={onClose} disabled={saving}>Cancel</button>
            <button style={btn('#27ae60')} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ item, rankNumber, dragEnabled, onDragStart, onDragOver, onDrop, onUpdate, onDelete, onViewDetails }) {
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState(item.admin_notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const cat = categoryMeta(item.category);
  const pri = priorityMeta(item.priority);
  const thm = themeMeta(item.theme);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await onUpdate(item.id, { admin_notes: notesDraft || null });
      setNotesOpen(false);
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div
      draggable={dragEnabled}
      onDragStart={dragEnabled ? onDragStart : undefined}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        background: 'white', border: '1px solid #e6e6e6', borderLeft: `3px solid ${cat.color}`,
        borderRadius: '5px', padding: '8px 9px', marginBottom: '7px',
        cursor: dragEnabled ? 'grab' : 'default', boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}>
          {rankNumber != null && (
            <span style={{ fontSize: '10.5px', fontWeight: 700, color: '#aaa', flex: 'none', marginTop: '1px' }}>#{rankNumber}</span>
          )}
          <div style={{ fontWeight: 600, color: '#2c3e50', fontSize: '12.5px', lineHeight: '1.3' }}>{item.title}</div>
        </div>
        <span onClick={() => onViewDetails(item)} title="View details" style={{ cursor: 'pointer', fontSize: '11px', color: '#bbb', flex: 'none' }}>🔍</span>
      </div>
      {thm ? (
        <div style={{ marginTop: '3px', fontSize: '10px', color: thm.color, background: thm.bg, display: 'inline-block', padding: '1px 5px', borderRadius: '3px' }}>{thm.label}</div>
      ) : (
        <div style={{ marginTop: '3px', fontSize: '10px', color: '#bbb', fontStyle: 'italic' }}>Untagged</div>
      )}
      {item.description && (
        <div style={{
          color: '#777', fontSize: '11.5px', marginTop: '3px', lineHeight: '1.35',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {item.description}
        </div>
      )}
      {item.requested_by_name && (
        <div style={{ marginTop: '4px', fontSize: '10.5px', color: '#888' }}>
          👤 {item.requested_by_name}{item.requested_by_role ? ` (${item.requested_by_role})` : ''}
        </div>
      )}

      {notesOpen && (
        <div style={{ marginTop: '6px' }}>
          <textarea
            style={{ ...input, width: '100%', minHeight: '50px', fontSize: '11.5px', fontFamily: 'inherit', resize: 'vertical' }}
            value={notesDraft}
            onChange={e => setNotesDraft(e.target.value)}
            placeholder="Notes for the weekly review…"
            autoFocus
          />
          <div style={{ marginTop: '4px', display: 'flex', gap: '6px' }}>
            <button style={{ ...btn('#27ae60'), padding: '2px 8px', fontSize: '11px' }} onClick={saveNotes} disabled={savingNotes}>{savingNotes ? 'Saving…' : 'Save'}</button>
            <button style={{ ...btn('#95a5a6'), padding: '2px 8px', fontSize: '11px' }} onClick={() => { setNotesOpen(false); setNotesDraft(item.admin_notes || ''); }}>Cancel</button>
          </div>
        </div>
      )}
      {!notesOpen && item.admin_notes && (
        <div onClick={() => setNotesOpen(true)} style={{ marginTop: '4px', fontSize: '10.5px', color: '#999', fontStyle: 'italic', cursor: 'pointer' }}>
          📝 {item.admin_notes}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px', gap: '4px', flexWrap: 'wrap' }}>
        <select
          style={{ fontSize: '10.5px', padding: '1px 3px', border: '1px solid #ddd', borderRadius: '3px', color: cat.color, background: cat.bg }}
          value={item.category}
          onChange={e => onUpdate(item.id, { category: e.target.value })}
        >
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select
          style={{ fontSize: '10.5px', padding: '1px 3px', border: '1px solid #ddd', borderRadius: '3px', color: pri ? pri.color : '#aaa', background: pri ? pri.bg : 'white' }}
          value={item.priority || ''}
          onChange={e => onUpdate(item.id, { priority: e.target.value || null })}
          title="Priority"
        >
          <option value="">Priority</option>
          {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select
          style={{ fontSize: '10.5px', padding: '1px 3px', border: '1px solid #ddd', borderRadius: '3px', color: item.assigned_to ? '#2c3e50' : '#aaa', background: 'white' }}
          value={item.assigned_to || ''}
          onChange={e => onUpdate(item.id, { assigned_to: e.target.value || null })}
          title="Assigned to"
        >
          <option value="">Unassigned</option>
          {ASSIGNEES.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select
          style={{ fontSize: '10.5px', padding: '1px 3px', border: '1px solid #ddd', borderRadius: '3px', color: thm ? thm.color : '#aaa', background: thm ? thm.bg : 'white' }}
          value={item.theme || ''}
          onChange={e => onUpdate(item.id, { theme: e.target.value || null })}
          title="Theme"
        >
          <option value="">Untagged</option>
          {THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {!item.admin_notes && !notesOpen && (
            <span onClick={() => setNotesOpen(true)} title="Add a note" style={{ cursor: 'pointer', fontSize: '11px', color: '#bbb' }}>📝</span>
          )}
          <span title={`Added ${fmt(item.created_at)}`} style={{ fontSize: '10px', color: '#ccc' }}>{fmt(item.created_at)}</span>
          {item.shipped_at && (
            <span title={`Shipped ${fmt(item.shipped_at)}`} style={{ fontSize: '10px', color: '#ccc' }}>· {fmt(item.shipped_at)}</span>
          )}
          <span onClick={() => onDelete(item)} title="Delete" style={{ cursor: 'pointer', fontSize: '11px', color: '#e0a0a0' }}>🗑</span>
        </div>
      </div>
    </div>
  );
}

function Column({ status, items, rankMap, dragEnabled, onDropOnCard, onDropOnColumn, onDragStartCard, onUpdate, onDelete, onAdd, onViewDetails }) {
  return (
    <div style={{ flex: `1 1 ${COLUMN_WIDTH}px`, minWidth: `${COLUMN_WIDTH}px`, display: 'flex', flexDirection: 'column', background: '#f4f5f7', borderRadius: '6px', maxHeight: 'calc(100vh - 260px)' }}>
      <div style={{ padding: '9px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e6e6e6' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{statusLabel(status)}</span>
        <span style={{ background: '#e0e2e6', color: '#666', borderRadius: '9px', padding: '1px 7px', fontSize: '11px' }}>{items.length}</span>
      </div>
      <div
        onDragOver={e => e.preventDefault()}
        onDrop={(e) => { e.stopPropagation(); onDropOnColumn(status); }}
        style={{ padding: '8px', overflowY: 'auto', flex: 1, minHeight: '60px' }}
      >
        {items.length === 0 && <div style={{ color: '#bbb', fontSize: '11.5px', textAlign: 'center', padding: '14px 4px' }}>No items</div>}
        {items.map(item => (
          <Card
            key={item.id}
            item={item}
            rankNumber={rankMap ? rankMap.get(item.id) : undefined}
            dragEnabled={dragEnabled}
            onDragStart={() => onDragStartCard(item.id, status)}
            onDragOver={e => e.preventDefault()}
            onDrop={(e) => { e.stopPropagation(); onDropOnCard(status, item.id); }}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onViewDetails={onViewDetails}
          />
        ))}
      </div>
      {status === 'New' && (
        <div style={{ padding: '6px 8px', borderTop: '1px solid #e6e6e6' }}>
          <button
            onClick={onAdd}
            style={{ width: '100%', background: 'transparent', border: 'none', color: '#888', fontSize: '11.5px', padding: '4px', cursor: 'pointer', textAlign: 'left' }}
          >
            + Add item
          </button>
        </div>
      )}
    </div>
  );
}

function NewListRow({ item, rank, dragEnabled, onDragStart, onDragOver, onDrop, onUpdate, onDelete, onViewDetails }) {
  const cat = categoryMeta(item.category);
  const pri = priorityMeta(item.priority);
  const thm = themeMeta(item.theme);
  return (
    <div
      draggable={dragEnabled}
      onDragStart={dragEnabled ? onDragStart : undefined}
      onDragOver={onDragOver}
      onDrop={onDrop}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 10px',
        background: 'white', border: '1px solid #e6e6e6', borderLeft: `3px solid ${cat.color}`,
        borderRadius: '5px', marginBottom: '6px', cursor: dragEnabled ? 'grab' : 'default',
      }}
    >
      <span style={{ fontWeight: 700, color: '#999', fontSize: '12px', width: '26px', flex: 'none', textAlign: 'right' }}>{rank != null ? `#${rank}` : ''}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#2c3e50', fontSize: '13px' }}>{item.title}</div>
        {item.description && (
          <div style={{ color: '#888', fontSize: '11.5px', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.description}
          </div>
        )}
      </div>
      <select
        style={{ fontSize: '10.5px', padding: '2px 4px', border: '1px solid #ddd', borderRadius: '3px', color: cat.color, background: cat.bg, flex: 'none' }}
        value={item.category}
        onChange={e => onUpdate(item.id, { category: e.target.value })}
      >
        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
      <select
        style={{ fontSize: '10.5px', padding: '2px 4px', border: '1px solid #ddd', borderRadius: '3px', color: pri ? pri.color : '#aaa', background: pri ? pri.bg : 'white', flex: 'none' }}
        value={item.priority || ''}
        onChange={e => onUpdate(item.id, { priority: e.target.value || null })}
        title="Priority"
      >
        <option value="">Priority</option>
        {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>
      <select
        style={{ fontSize: '10.5px', padding: '2px 4px', border: '1px solid #ddd', borderRadius: '3px', color: item.assigned_to ? '#2c3e50' : '#aaa', background: 'white', flex: 'none' }}
        value={item.assigned_to || ''}
        onChange={e => onUpdate(item.id, { assigned_to: e.target.value || null })}
        title="Assigned to"
      >
        <option value="">Unassigned</option>
        {ASSIGNEES.map(a => <option key={a} value={a}>{a}</option>)}
      </select>
      <select
        style={{ fontSize: '10.5px', padding: '2px 4px', border: '1px solid #ddd', borderRadius: '3px', color: thm ? thm.color : '#aaa', background: thm ? thm.bg : 'white', flex: 'none' }}
        value={item.theme || ''}
        onChange={e => onUpdate(item.id, { theme: e.target.value || null })}
        title="Theme"
      >
        <option value="">Untagged</option>
        {THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <button
        onClick={() => onUpdate(item.id, { status: 'Under Review' })}
        style={{ ...btn('#2c5aa0'), padding: '3px 8px', fontSize: '10.5px', flex: 'none', whiteSpace: 'nowrap' }}
        title="Move to Under Review"
      >
        → Under Review
      </button>
      <span onClick={() => onViewDetails(item)} title="View details" style={{ cursor: 'pointer', fontSize: '12px', color: '#bbb', flex: 'none' }}>🔍</span>
      <span onClick={() => onDelete(item)} title="Delete" style={{ cursor: 'pointer', fontSize: '12px', color: '#e0a0a0', flex: 'none' }}>🗑</span>
    </div>
  );
}

// ShippedListRow — "All Shipped" full-history row. Chronological by
// shipped_at, not manually ranked, so unlike NewListRow there's no drag
// handling and nowhere further to promote an item to.
function ShippedListRow({ item, onUpdate, onDelete, onViewDetails }) {
  const cat = categoryMeta(item.category);
  const thm = themeMeta(item.theme);
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 10px',
        background: 'white', border: '1px solid #e6e6e6', borderLeft: `3px solid ${cat.color}`,
        borderRadius: '5px', marginBottom: '6px',
      }}
    >
      <span style={{ fontSize: '11px', color: '#999', width: '60px', flex: 'none' }}>{fmt(item.shipped_at)}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: '#2c3e50', fontSize: '13px' }}>{item.title}</div>
        {item.description && (
          <div style={{ color: '#888', fontSize: '11.5px', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.description}
          </div>
        )}
      </div>
      <select
        style={{ fontSize: '10.5px', padding: '2px 4px', border: '1px solid #ddd', borderRadius: '3px', color: cat.color, background: cat.bg, flex: 'none' }}
        value={item.category}
        onChange={e => onUpdate(item.id, { category: e.target.value })}
      >
        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
      </select>
      <select
        style={{ fontSize: '10.5px', padding: '2px 4px', border: '1px solid #ddd', borderRadius: '3px', color: item.assigned_to ? '#2c3e50' : '#aaa', background: 'white', flex: 'none' }}
        value={item.assigned_to || ''}
        onChange={e => onUpdate(item.id, { assigned_to: e.target.value || null })}
        title="Assigned to"
      >
        <option value="">Unassigned</option>
        {ASSIGNEES.map(a => <option key={a} value={a}>{a}</option>)}
      </select>
      <select
        style={{ fontSize: '10.5px', padding: '2px 4px', border: '1px solid #ddd', borderRadius: '3px', color: thm ? thm.color : '#aaa', background: thm ? thm.bg : 'white', flex: 'none' }}
        value={item.theme || ''}
        onChange={e => onUpdate(item.id, { theme: e.target.value || null })}
        title="Theme"
      >
        <option value="">Untagged</option>
        {THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <span onClick={() => onViewDetails(item)} title="View details" style={{ cursor: 'pointer', fontSize: '12px', color: '#bbb', flex: 'none' }}>🔍</span>
      <span onClick={() => onDelete(item)} title="Delete" style={{ cursor: 'pointer', fontSize: '12px', color: '#e0a0a0', flex: 'none' }}>🗑</span>
    </div>
  );
}

export default function BacklogQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [themeFilter, setThemeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [showListView, setShowListView] = useState(false);
  const [showShippedList, setShowShippedList] = useState(false);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [sortMode, setSortMode] = useState('order');
  const dragInfo = useRef({ id: null, fromStatus: null });
  const dragEnabled = sortMode === 'order';

  const load = useCallback(() => {
    setLoading(true);
    return axios.get(`${API}/backlog/`)
      .then(res => { setItems(Array.isArray(res.data) ? res.data : []); setErr(null); })
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load the backlog queue'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const createItem = async (payload) => {
    await axios.post(`${API}/backlog/`, payload);
    await load();
  };

  const updateItem = async (id, patch) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
    try {
      const res = await axios.put(`${API}/backlog/${id}`, patch);
      setItems(prev => prev.map(i => i.id === id ? res.data : i));
    } catch (e) {
      alert('Update failed: ' + (e.response?.data?.detail || e.message));
      await load();
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Remove "${item.title}" from the backlog queue? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/backlog/${item.id}`);
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (e) {
      alert('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const matches = (item) =>
    (!categoryFilter || item.category === categoryFilter) &&
    (!themeFilter || (themeFilter === '__untagged__' ? !item.theme : item.theme === themeFilter)) &&
    (!search || item.title.toLowerCase().includes(search.toLowerCase()) || (item.description || '').toLowerCase().includes(search.toLowerCase()));

  const inColumn = (status) => items.filter(i => i.status === status).sort((a, b) => (a.rank ?? 999999) - (b.rank ?? 999999));
  const visibleInColumn = (status) => inColumn(status).filter(matches);

  // Display-only re-sorts layered on top of the rank-ordered column lists.
  // Never applied inside dropInto()/applyReorder() — those always operate on
  // the manual rank order (and drag is disabled outside "order" mode anyway),
  // so the stored ranks are untouched by viewing in Priority/Owner mode.
  const byRank = (a, b) => (a.rank ?? 999999) - (b.rank ?? 999999);
  const byPriority = (a, b) => (PRIORITY_RANK[a.priority] ?? 99) - (PRIORITY_RANK[b.priority] ?? 99);
  const byOwner = (a, b) => (a.assigned_to || '\uffff').localeCompare(b.assigned_to || '\uffff'); // unassigned sorts last
  const applySortMode = (arr) => {
    if (sortMode === 'priority') return [...arr].sort((a, b) => byPriority(a, b) || byRank(a, b));
    if (sortMode === 'owner') return [...arr].sort((a, b) => byOwner(a, b) || byPriority(a, b) || byRank(a, b));
    return arr;
  };

  // "Shipped This Week" — the kanban column only shows items shipped in the
  // current Monday–Sunday week; drag-and-drop still operates on the full
  // unfiltered inColumn('Shipped') list (see dropInto/applyReorder below),
  // so dropping a card here is unaffected by this display-only restriction.
  const currentWeekStart = getWeekStart(new Date());
  const shippedThisWeek = () => inColumn('Shipped').filter(i => isInWeek(i.shipped_at, currentWeekStart));

  // "All Shipped" — full history, newest first.
  const shippedFull = [...inColumn('Shipped')].sort((a, b) => new Date(b.shipped_at || 0) - new Date(a.shipped_at || 0));
  const shippedVisible = shippedFull.filter(matches);

  // Release Notes — shipped items grouped by the week they shipped, newest
  // week first. Items with no shipped_at (shouldn't happen post-backfill,
  // but defensively) are skipped rather than crashing the grouping.
  const releaseNoteWeeks = () => {
    const groups = new Map();
    for (const item of inColumn('Shipped')) {
      if (!item.shipped_at) continue;
      const iso = item.shipped_at.endsWith('Z') || item.shipped_at.includes('+') ? item.shipped_at : item.shipped_at + 'Z';
      const weekStart = getWeekStart(new Date(iso));
      const key = weekStart.getTime();
      if (!groups.has(key)) groups.set(key, { weekStart, items: [] });
      groups.get(key).items.push(item);
    }
    return [...groups.values()].sort((a, b) => b.weekStart - a.weekStart);
  };

  const totalVisible = items.filter(matches).length;

  // "New" items get a visible #rank badge (both in the kanban card and the list
  // view) computed from their live position in the full, unfiltered column —
  // so it stays contiguous (1,2,3…) and re-numbers itself the moment an item
  // is dragged out to Under Review, with no extra backend call needed.
  const newFull = inColumn('New');
  const newRankMap = new Map(newFull.map((it, idx) => [it.id, idx + 1]));
  const newVisible = visibleInColumn('New');

  const applyReorder = async (status, ids, statusChangedId) => {
    // Optimistic local update first so the drag feels instant.
    setItems(prev => prev.map(i => {
      if (statusChangedId && i.id === statusChangedId) {
        const idx = ids.indexOf(i.id);
        return { ...i, status, rank: (idx + 1) * 10 };
      }
      const idx = ids.indexOf(i.id);
      return idx >= 0 ? { ...i, rank: (idx + 1) * 10 } : i;
    }));
    try {
      if (statusChangedId) {
        await axios.put(`${API}/backlog/${statusChangedId}`, { status });
      }
      await axios.put(`${API}/backlog/reorder`, { status, ids });
    } catch (e) {
      alert('Reorder failed: ' + (e.response?.data?.detail || e.message));
      await load();
    }
  };

  const handleDragStartCard = (id, fromStatus) => { dragInfo.current = { id, fromStatus }; };

  const dropInto = (targetStatus, targetItemId /* null = end of column */) => {
    if (!dragEnabled) return;
    const { id: draggedId, fromStatus } = dragInfo.current;
    dragInfo.current = { id: null, fromStatus: null };
    if (!draggedId || draggedId === targetItemId) return;

    const targetIds = inColumn(targetStatus).map(i => i.id).filter(id => id !== draggedId);
    if (targetItemId != null) {
      const dropIndex = targetIds.indexOf(targetItemId);
      targetIds.splice(dropIndex < 0 ? targetIds.length : dropIndex, 0, draggedId);
    } else {
      targetIds.push(draggedId);
    }

    applyReorder(targetStatus, targetIds, fromStatus !== targetStatus ? draggedId : null);
  };

  return (
    <div style={{ width: '100%' }}>
      <h2 style={{ color: '#2c3e50', marginBottom: '4px' }}>Backlog Queue</h2>
      <p style={{ color: '#7f8c8d', fontSize: '13px', marginTop: 0, maxWidth: '900px' }}>
        Every feature request, bug report, and internally-added item, reviewed weekly. Drag a card to re-prioritize it
        within a column, or drag it into a different column to move it through review. Admin only.
      </p>

      {err && <div style={{ background: '#fdecea', color: '#c0392b', padding: '8px 12px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>{err}</div>}

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
        <select style={select} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
          <option value="">All types</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select style={select} value={themeFilter} onChange={e => setThemeFilter(e.target.value)}>
          <option value="">All themes</option>
          <option value="__untagged__">Untagged</option>
          {THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input style={{ ...input, minWidth: '220px' }} placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '12px', color: '#7f8c8d' }}>Sort:</span>
          {SORT_MODES.map(m => (
            <button
              key={m.value}
              style={{ ...btn(sortMode === m.value ? '#2c5aa0' : '#bdc3c7'), padding: '5px 9px' }}
              onClick={() => setSortMode(m.value)}
              title={m.value === 'order' ? 'Manual drag order' : `View sorted by ${m.label.toLowerCase()} (drag-to-reorder off in this view)`}
            >
              {m.label}
            </button>
          ))}
        </span>
        <button style={btn('#7f8c8d')} disabled={loading} onClick={load}>↻ Refresh</button>
        <button style={btn('#2c5aa0')} onClick={() => setShowAddModal(true)}>+ Add Item</button>
        <button style={btn(showListView ? '#2c5aa0' : '#95a5a6')} onClick={() => setShowListView(v => !v)}>
          {showListView ? '▾' : '▸'} List View — Backlog ({newFull.length})
        </button>
        <button style={btn(showShippedList ? '#2c5aa0' : '#95a5a6')} onClick={() => setShowShippedList(v => !v)}>
          {showShippedList ? '▾' : '▸'} List View — All Shipped ({shippedFull.length})
        </button>
        <button style={btn(showReleaseNotes ? '#2c5aa0' : '#95a5a6')} onClick={() => setShowReleaseNotes(v => !v)}>
          {showReleaseNotes ? '▾' : '▸'} 📄 Release Notes
        </button>
        <span style={{ marginLeft: 'auto', color: '#95a5a6', fontSize: '12px' }}>{totalVisible} item{totalVisible === 1 ? '' : 's'}</span>
      </div>

      {loading ? (
        <div style={{ padding: '24px', color: '#999' }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '8px' }}>
          {STATUSES.map(status => (
            <Column
              key={status}
              status={status}
              items={applySortMode(status === 'Shipped' ? shippedThisWeek().filter(matches) : visibleInColumn(status))}
              rankMap={status === 'New' && sortMode === 'order' ? newRankMap : null}
              dragEnabled={dragEnabled}
              onDragStartCard={handleDragStartCard}
              onDropOnCard={(targetStatus, targetItemId) => dropInto(targetStatus, targetItemId)}
              onDropOnColumn={(targetStatus) => dropInto(targetStatus, null)}
              onUpdate={updateItem}
              onDelete={deleteItem}
              onAdd={() => setShowAddModal(true)}
              onViewDetails={setDetailItem}
            />
          ))}
        </div>
      )}

      {showListView && (
        <div style={{ marginTop: '16px', maxWidth: '1400px' }}>
          <h3 style={{ color: '#2c3e50', fontSize: '14px', marginBottom: '4px' }}>Backlog — Ranked List View</h3>
          <p style={{ color: '#7f8c8d', fontSize: '12px', marginTop: 0, marginBottom: '10px' }}>
            Drag a row to re-prioritize it, or send it to Under Review once it's ready for a closer look.
          </p>
          {newVisible.length === 0 && (
            <div style={{ color: '#bbb', fontSize: '12px', padding: '10px' }}>No Backlog items match the current filter.</div>
          )}
          {applySortMode(newVisible).map(item => (
            <NewListRow
              key={item.id}
              item={item}
              rank={sortMode === 'order' ? newRankMap.get(item.id) : null}
              dragEnabled={dragEnabled}
              onDragStart={() => handleDragStartCard(item.id, 'New')}
              onDragOver={e => e.preventDefault()}
              onDrop={(e) => { e.stopPropagation(); dropInto('New', item.id); }}
              onUpdate={updateItem}
              onDelete={deleteItem}
              onViewDetails={setDetailItem}
            />
          ))}
          {newVisible.length > 0 && (
            <div
              onDragOver={e => e.preventDefault()}
              onDrop={(e) => { e.stopPropagation(); dropInto('New', null); }}
              style={{ height: '20px' }}
            />
          )}
        </div>
      )}

      {showShippedList && (
        <div style={{ marginTop: '16px', maxWidth: '1400px' }}>
          <h3 style={{ color: '#2c3e50', fontSize: '14px', marginBottom: '4px' }}>All Shipped — Full History</h3>
          <p style={{ color: '#7f8c8d', fontSize: '12px', marginTop: 0, marginBottom: '10px' }}>
            Every item ever marked Shipped, newest first — the "Shipped This Week" column above only shows the current week.
          </p>
          {shippedVisible.length === 0 && (
            <div style={{ color: '#bbb', fontSize: '12px', padding: '10px' }}>No Shipped items match the current filter.</div>
          )}
          {shippedVisible.map(item => (
            <ShippedListRow
              key={item.id}
              item={item}
              onUpdate={updateItem}
              onDelete={deleteItem}
              onViewDetails={setDetailItem}
            />
          ))}
        </div>
      )}

      {showReleaseNotes && (
        <div style={{ marginTop: '16px', maxWidth: '1100px' }}>
          <h3 style={{ color: '#2c3e50', fontSize: '14px', marginBottom: '4px' }}>Release Notes</h3>
          <p style={{ color: '#7f8c8d', fontSize: '12px', marginTop: 0, marginBottom: '14px' }}>
            What shipped, grouped by week — copy/paste-ready for a team update.
          </p>
          {releaseNoteWeeks().length === 0 && (
            <div style={{ color: '#bbb', fontSize: '12px', padding: '10px' }}>Nothing shipped yet.</div>
          )}
          {releaseNoteWeeks().map(({ weekStart, items: weekItems }) => (
            <div key={weekStart.getTime()} style={{ marginBottom: '18px' }}>
              <div style={{ fontWeight: 700, color: '#2c3e50', fontSize: '13px', marginBottom: '6px' }}>{weekLabel(weekStart)}</div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {weekItems.map(item => (
                  <li key={item.id} style={{ color: '#444', fontSize: '13px', marginBottom: '4px', lineHeight: '1.4' }}>
                    <strong>{item.title}</strong>{item.description ? `: ${item.description}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddItemModal
          onClose={() => setShowAddModal(false)}
          onCreate={(payload) => createItem(payload)}
        />
      )}

      {detailItem && (
        <DetailModal
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onUpdate={updateItem}
          onDelete={deleteItem}
        />
      )}
    </div>
  );
}
